import { isWeekend } from "../../../../../utils";
import { ISalesStatsByDayResponse, IReportStats } from "../../../../../api/billing/index.types";
import { ReportStrategy } from "./reportBuilder";

const HEADER_ROW = [
  "Date",
  "Amount",
  "Tradovate Amount",
  "Rithmic Amount",
  "Quantity",
  "Product name",
  "Transaction type",
];

export class BasicSalesReportStrategy implements ReportStrategy {
  private readonly outputRows: (string | number)[][] = [HEADER_ROW];
  private readonly sourceData: ISalesStatsByDayResponse["data"]["report"] = [];
  private readonly skipWeekends: boolean = false;
  private readonly tradovateByDate: Map<string, IReportStats>;
  private readonly rithmicByDate: Map<string, IReportStats>;

  private readonly planCodeMap: [string, string][] = [
    ["Gauntlet Mini 50K", "gau50"],
    ["Gauntlet Mini 100K", "gau100"],
    ["Gauntlet Mini 150K", "gau150"],
    ["Gauntlet Mini 200K", "gau200"],
    ["Trader Career Path (25K)", "tcp25"],
    ["Trader Career Path (50K)", "tcp50"],
    ["Trader Career Path (100K)", "tcp100"],
  ];

  constructor(
    reportData: ISalesStatsByDayResponse["data"]["report"],
    skipWeekends: boolean,
    tradovateData?: ISalesStatsByDayResponse["data"]["report"],
    rithmicData?: ISalesStatsByDayResponse["data"]["report"]
  ) {
    this.sourceData = reportData;
    this.skipWeekends = skipWeekends;
    this.tradovateByDate = BasicSalesReportStrategy.buildDateMap(tradovateData);
    this.rithmicByDate = BasicSalesReportStrategy.buildDateMap(rithmicData);
  }

  private static buildDateMap(
    data?: ISalesStatsByDayResponse["data"]["report"]
  ): Map<string, IReportStats> {
    const map = new Map<string, IReportStats>();
    if (data) data.forEach((row) => map.set(row.date, row));
    return map;
  }

  private getBrokerAmount(
    brokerMap: Map<string, IReportStats>,
    date: string,
    prefix: string,
    type: "new" | "reset" | "rebill"
  ): number {
    const row = brokerMap.get(date);
    if (!row) return 0;
    if (type === "new") {
      return (
        Number(row[`${prefix}_net_us` as keyof IReportStats] ?? 0) +
        Number(row[`${prefix}_net_not_us` as keyof IReportStats] ?? 0)
      );
    }
    if (type === "reset") {
      return (
        Number(row[`${prefix}_reset_net_us` as keyof IReportStats] ?? 0) +
        Number(row[`${prefix}_reset_net_not_us` as keyof IReportStats] ?? 0)
      );
    }
    return (
      Number(row[`${prefix}_rebill_net_us` as keyof IReportStats] ?? 0) +
      Number(row[`${prefix}_rebill_net_not_us` as keyof IReportStats] ?? 0)
    );
  }

  populateRows(): void {
    this.sourceData.forEach((row: IReportStats) => {
      const { date } = row;
      const emptyRow = new Array(HEADER_ROW.length).fill(" ");
      const { skipWeekends } = this;

      for (let i = 0; i < this.planCodeMap.length; i++) {
        const [planName, prefix] = this.planCodeMap[i];

        const resetAmount =
          Number(row[`${prefix}_reset_net_us` as keyof IReportStats]) +
          Number(row[`${prefix}_reset_net_not_us` as keyof IReportStats]);
        const resetQuantity = row[`${prefix}_paid_reset_qt` as keyof IReportStats];
        const freeResetsQuantity = row[`${prefix}_free_reset_qt` as keyof IReportStats];

        const newPurchaseAmount =
          Number(row[`${prefix}_net_us` as keyof IReportStats]) +
          Number(row[`${prefix}_net_not_us` as keyof IReportStats]);
        const newPurchaseQuantity = row[`${prefix}_qt` as keyof IReportStats];

        const rebilAmount =
          Number(row[`${prefix}_rebill_net_us` as keyof IReportStats]) +
          Number(row[`${prefix}_rebill_net_not_us` as keyof IReportStats]);
        const rebilQuantity = row[`${prefix}_rebill_qt` as keyof IReportStats];

        const productCollectionStats = [
          [
            date,
            resetAmount,
            this.getBrokerAmount(this.tradovateByDate, date, prefix, "reset"),
            this.getBrokerAmount(this.rithmicByDate, date, prefix, "reset"),
            resetQuantity,
            planName,
            "Reset",
          ],
          [date, 0, 0, 0, freeResetsQuantity, planName, "Free reset"],
          [
            date,
            newPurchaseAmount,
            this.getBrokerAmount(this.tradovateByDate, date, prefix, "new"),
            this.getBrokerAmount(this.rithmicByDate, date, prefix, "new"),
            newPurchaseQuantity,
            planName,
            "New sales",
          ],
          [
            date,
            rebilAmount,
            this.getBrokerAmount(this.tradovateByDate, date, prefix, "rebill"),
            this.getBrokerAmount(this.rithmicByDate, date, prefix, "rebill"),
            rebilQuantity,
            planName,
            "Rebil",
          ],
        ];

        if (!skipWeekends) {
          this.outputRows.push(...productCollectionStats);
        }
        if (skipWeekends && !isWeekend(new Date(date))) {
          this.outputRows.push(...productCollectionStats);
        }
      }

      if (!skipWeekends) {
        this.outputRows.push(emptyRow);
      }
      if (skipWeekends && !isWeekend(new Date(date))) {
        this.outputRows.push(emptyRow);
      }
    });
  }

  generate(): (string | number)[][] {
    this.populateRows();
    return this.outputRows;
  }
}
